// MCHD Room Scheduler - Audit Logs Hook
// Custom hook for managing audit logs and reporting functionality

import { useState, useEffect, useCallback } from 'react';
import { 
  getAuditLogs, 
  getAuditReport, 
  exportAuditLogs,
  AuditLogFilters,
  AuditLogsResponse,
  AuditReportResponse,
  ExportResponse,
  ExportOptions
} from '../../api/endpoints/auth';

export interface AuditLogStats {
  totalLogs: number;
  uniqueUsers: number;
  eventTypes: string[];
  dateRange: {
    start: string;
    end: string;
  };
}

/**
 * Default empty stats to prevent undefined errors
 */
export const DEFAULT_AUDIT_STATS: AuditLogStats = {
  totalLogs: 0,
  uniqueUsers: 0,
  eventTypes: [],
  dateRange: {
    start: '',
    end: '',
  },
};

export interface AuditReport {
  summary: {
    totalActions: number;
    uniqueUsers: number;
    dateRange: {
      start: string;
      end: string;
    };
  };
  actions: {
    [key: string]: number;
  };
  users: {
    [key: string]: number;
  };
}

/**
 * Custom hook for audit logs management
 * @returns Audit logs state and functions
 */
export function useAuditLogs() {
  const [logs, setLogs] = useState<any[]>([]);
  const [stats, setStats] = useState<AuditLogStats>(DEFAULT_AUDIT_STATS);
  const [report, setReport] = useState<AuditReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch audit logs with filters
   * @param filters - Audit log filters
   */
  const fetchLogs = useCallback(async (filters: AuditLogFilters = {}) => {

    setIsLoading(true);
    setError(null);

    try {
      const response: AuditLogsResponse = await getAuditLogs(filters);
      
      if (response.success && response.data) {
        setLogs(response.data.logs || []);
        setStats({
          totalLogs: response.data.stats?.totalLogs || 0,
          uniqueUsers: response.data.stats?.uniqueUsers || 0,
          eventTypes: Array.isArray(response.data.stats?.eventTypes) ? response.data.stats.eventTypes : [],
          dateRange: filters.startDate && filters.endDate ? {
            start: filters.startDate,
            end: filters.endDate,
          } : {
            start: '',
            end: '',
          },
        });
      } else {
        setError(response.error?.message || 'Failed to fetch audit logs');
        // Reset to default stats on error
        setStats(DEFAULT_AUDIT_STATS);
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError('Failed to fetch audit logs');
      // Reset to default stats on error
      setStats(DEFAULT_AUDIT_STATS);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Generate audit report for date range
   * @param startDate - Report start date
   * @param endDate - Report end date
   */
  const generateReport = useCallback(async (startDate: string, endDate: string) => {

    setIsGeneratingReport(true);
    setError(null);

    try {
      const response: AuditReportResponse = await getAuditReport(startDate, endDate);
      
      if (response.success && response.data) {
        setReport(response.data.report);
      } else {
        setError(response.error?.message || 'Failed to generate audit report');
      }
    } catch (err) {
      console.error('Error generating audit report:', err);
      setError('Failed to generate audit report');
    } finally {
      setIsGeneratingReport(false);
    }
  }, []);

  /**
   * Export audit logs
   * @param options - Export options
   */
  const exportLogs = useCallback(async (options: ExportOptions = {}) => {

    setIsExporting(true);
    setError(null);

    try {
      const response: ExportResponse = await exportAuditLogs(options);
      
      if (response.success && response.data) {
        return response.data;
      } else {
        setError(response.error?.message || 'Failed to export audit logs');
        return null;
      }
    } catch (err) {
      console.error('Error exporting audit logs:', err);
      setError('Failed to export audit logs');
      return null;
    } finally {
      setIsExporting(false);
    }
  }, []);

  /**
   * Clear audit logs data
   */
  const clearData = useCallback(() => {
    setLogs([]);
    setStats({
      totalLogs: 0,
      uniqueUsers: 0,
      eventTypes: [],
      dateRange: {
        start: '',
        end: '',
      },
    });
    setReport(null);
    setError(null);
  }, []);

  /**
   * Refresh audit logs data
   * @param filters - Current filters to reapply
   */
  const refresh = useCallback((filters: AuditLogFilters = {}) => {
    fetchLogs(filters);
  }, [fetchLogs]);

  // Auto-fetch logs on mount with default filters
  useEffect(() => {
    const defaultFilters: AuditLogFilters = {
      limit: 100,
    };
    fetchLogs(defaultFilters);
  }, [fetchLogs]);

  return {
    logs,
    stats,
    report,
    isLoading,
    isGeneratingReport,
    isExporting,
    error,
    fetchLogs,
    generateReport,
    exportLogs,
    clearData,
    refresh,
  };
}
